import { Link, useNavigate } from 'react-router-dom';
import { useActivity, useGlobalStatistics } from '../hooks/useStatistics';
import { useCourses, useJoinCourse } from '../hooks/useCourses';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Karti } from '../components/brand/Karti';
import { KartiCoachBubble, KartiState } from '../components/brand/KartiCoach';
import { activityContext } from '../lib/karti';
import { GroupBar, CourseGlyph } from '../components/brand/GroupBar';
import { useAuthStore } from '../store/authStore';

const ROLE_LABEL: Record<string, string> = { owner: 'OWNER', editor: 'EDITOR', member: 'MITGLIED' };

function SectionLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="label-mono !text-xs">{children}</h2>
      {action}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuthStore();
  const { data: globalStats, isLoading: statsLoading } = useGlobalStatistics();
  const { data: activity } = useActivity();
  const { data: courses, isLoading: coursesLoading } = useCourses();
  const joinCourseMutation = useJoinCourse();

  const myCourses = courses?.filter((c) => c.role) || [];
  const publicCourses = courses?.filter((c) => !c.isPrivate && !c.role) || [];
  const totalVocabularies = myCourses.reduce((sum, c) => sum + c.vocabularyCount, 0);

  const recentCourses = [...myCourses]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 4);

  const trendingCourses = [...publicCourses].sort((a, b) => b.memberCount - a.memberCount).slice(0, 6);

  const handleJoinCourse = async (courseId: number) => {
    try {
      await joinCourseMutation.mutateAsync(courseId);
      showToast('Erfolgreich dem Kurs beigetreten', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Beitreten', 'error');
    }
  };

  if (coursesLoading || statsLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        <KartiState screen="loading" size={72} />
        <div className="skeleton h-5 w-40" />
        <div className="skeleton h-12 w-4/5" />
        <div className="skeleton h-24 w-full !rounded-panel" />
        <div className="skeleton h-24 w-full !rounded-panel" />
      </div>
    );
  }

  const dueByCourse = new Map((activity?.due.courses ?? []).map((c) => [c.courseId, c.due]));
  const continueCourse = [...myCourses].sort((a, b) => (dueByCourse.get(b.id) ?? 0) - (dueByCourse.get(a.id) ?? 0))[0];
  const continueDue = continueCourse ? (dueByCourse.get(continueCourse.id) ?? 0) : 0;
  const totalLearned = globalStats?.reduce((sum, s) => sum + s.learnedVocabularies, 0) || 0;
  const totalInProgress = globalStats?.reduce((sum, s) => sum + s.inProgress, 0) || 0;
  const firstName = (user?.displayname || user?.nickname || '').split(' ')[0];
  const today = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: 'short' })
    .format(new Date())
    .replace(/\./g, '')
    .toUpperCase();

  return (
    <div className="space-y-5 animate-fadein">
      {/* Greeting */}
      <div>
        <div className="label-mono">{today}</div>
        <div className="text-[17px] font-bold">Hallo {firstName}</div>
      </div>

      <div className="flex items-start justify-between gap-3">
        <h1 className="text-[34px] font-extrabold leading-none tracking-[-0.035em] sm:text-[38px]">
          {activity ? (
            activity.due.total > 0 ? (
              <>
                Heute sind <span className="highlight">{activity.due.total} Karten</span> fällig.
              </>
            ) : (
              <>
                Heute <span className="highlight">alles erledigt.</span>
              </>
            )
          ) : totalInProgress > 0 ? (
            <>
              <span className="highlight">{totalInProgress} Vokabeln</span> sind gerade in Arbeit.
            </>
          ) : (
            <>
              Dein <span className="highlight">Lernfortschritt</span> im Überblick.
            </>
          )}
        </h1>
        {activity && (
          <div className="-rotate-[4deg] shrink-0 rounded-[12px] border-[2.5px] border-ink bg-marker px-2.5 py-1.5 text-center shadow-hard-sm">
            <span className="text-[20px] font-extrabold leading-none">{activity.streak}</span>{' '}
            <span className="font-mono text-[10px] font-bold">TAGE</span>
          </div>
        )}
      </div>

      <KartiCoachBubble screen="dashboard" ctx={activityContext(activity)} />

      {continueCourse && continueDue > 0 && (
        <Link
          to={`/app/courses/${continueCourse.id}/quiz`}
          className="press block rounded-hero border-[2.5px] border-ink bg-ink p-[18px] text-paper shadow-hard"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[11px] font-bold tracking-[0.07em] text-on-ink-sub">WEITER LERNEN</div>
              <div className="truncate text-[22px] font-bold">{continueCourse.name}</div>
              <div className="text-sm text-on-ink-sub">
                {continueDue} fällig · ca. {Math.max(1, Math.round(continueDue / 4))} Min.
              </div>
            </div>
            <CourseGlyph name={continueCourse.name} active size={56} className="!text-ink" />
          </div>
          <div className="mt-4 flex justify-end">
            <span className="rounded-[14px] bg-marker px-4 py-2.5 text-[15px] font-extrabold text-ink">Los →</span>
          </div>
        </Link>
      )}

      {/* App notice */}
      <div className="rounded-list border-2.5 border-ink bg-cream p-4 shadow-hard">
        <div className="flex items-start gap-3">
          <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full border-2 border-ink bg-wrong" />
          <div className="space-y-1">
            <h3 className="text-[17px] font-bold">Web-Funktionen werden nicht mehr weiterentwickelt</h3>
            <p className="text-sm leading-relaxed text-body">
              Wir konzentrieren uns jetzt voll auf die mobile App-Erfahrung. Diese Web-Version erhält keine neuen
              Features mehr. Für die beste Lernerfahrung empfehlen wir dir, die VOC7-App zu nutzen.
            </p>
          </div>
        </div>
      </div>

      {/* Ink summary panel */}
      <div className="grid grid-cols-2 gap-3 rounded-hero bg-ink p-4 text-paper sm:grid-cols-4">
        {[
          { v: myCourses.length, l: 'Kurse', accent: true, onClick: () => navigate('/app/courses') },
          { v: totalVocabularies, l: 'Vokabeln' },
          { v: totalInProgress, l: 'In Arbeit', sub: 'Phase 3–5' },
          { v: totalLearned, l: 'Gelernt', sub: 'Phase 6–7' },
        ].map((s) => (
          <div key={s.l} onClick={s.onClick} className={s.onClick ? 'cursor-pointer' : undefined}>
            <div className={`text-[40px] font-extrabold leading-none tracking-tight ${s.accent ? 'text-marker' : ''}`}>
              {s.v.toLocaleString('de-DE')}
            </div>
            <div className="mt-1 font-mono text-[11px] font-bold uppercase tracking-[0.07em] text-on-ink-sub">
              {s.l}
              {s.sub ? ` · ${s.sub}` : ''}
            </div>
          </div>
        ))}
      </div>

      {/* My courses */}
      <SectionLabel
        action={
          <button
            onClick={() => navigate('/app/courses')}
            className="press h-9 rounded-tile border-2 border-ink bg-cream px-3.5 text-sm font-bold shadow-hard-sm"
          >
            Alle anzeigen
          </button>
        }
      >
        MEINE KURSE · {myCourses.length}
      </SectionLabel>

      {myCourses.length === 0 ? (
        <div className="rounded-panel border-2.5 border-ink bg-cream p-8 text-center shadow-hard">
          <Karti className="mx-auto mb-3" size={96} mood="sleep" />
          <h3 className="text-[22px] font-bold">Noch keine Kurse</h3>
          <p className="mb-5 mt-1 text-body">Erstelle deinen ersten Kurs und beginne mit dem Lernen</p>
          <Button variant="accent" onClick={() => navigate('/app/courses')}>
            Ersten Kurs erstellen
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {recentCourses.map((course, i) => (
            <div
              key={course.id}
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/app/courses/${course.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/app/courses/${course.id}`)}
              className="press cursor-pointer rounded-list border-2.5 border-ink bg-cream p-3.5 shadow-hard"
            >
              <div className="flex items-center gap-3">
                <CourseGlyph name={course.name} active={i === 0} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[17px] font-bold">{course.name}</div>
                  <div className="text-[13px] text-muted-foreground">
                    {course.vocabularyCount} Vokabeln · {course.memberCount} Mitglieder
                  </div>
                </div>
                <span className="rounded-chip border-[1.5px] border-ink px-[7px] py-1 font-mono text-[10px] font-bold">
                  {ROLE_LABEL[course.role] ?? course.role}
                </span>
              </div>
              {course.vocabularyCount >= 15 && (
                <Button
                  variant="accent"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/app/courses/${course.id}/quiz`);
                  }}
                >
                  Quiz starten →
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Global progress */}
      {globalStats && globalStats.length > 0 && (
        <>
          <SectionLabel>LERNFORTSCHRITT</SectionLabel>
          <div className="space-y-4 rounded-panel border-2.5 border-ink bg-cream p-4">
            {globalStats.map((stat) => (
              <div key={stat.courseId} className="space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="truncate text-[15px] font-bold">{stat.courseName}</h3>
                  <span className="font-mono text-[15px] font-bold">{stat.overallProgress.toFixed(0)}%</span>
                </div>
                <GroupBar neu={stat.new} inProgress={stat.inProgress} learned={stat.learnedVocabularies} />
                <div className="flex gap-4 font-mono text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  <span>Neu {stat.new}</span>
                  <span>In Arbeit {stat.inProgress}</span>
                  <span>Gelernt {stat.learnedVocabularies}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Public courses */}
      <SectionLabel
        action={
          <button
            onClick={() => navigate('/app/courses')}
            className="press h-9 rounded-tile border-2 border-ink bg-cream px-3.5 text-sm font-bold shadow-hard-sm"
          >
            Alle durchsuchen
          </button>
        }
      >
        BELIEBTE KURSE
      </SectionLabel>
      {trendingCourses.length === 0 ? (
        <div className="rounded-panel border-2.5 border-ink bg-cream p-6 text-center">
          <h3 className="text-lg font-bold">Keine öffentlichen Kurse verfügbar</h3>
          <p className="mb-4 mt-1 text-sm text-body">Erstelle einen öffentlichen Kurs oder warte auf neue Kurse</p>
          <Button variant="outline" onClick={() => navigate('/app/courses')}>
            Kurs erstellen
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {trendingCourses.map((course) => (
            <div
              key={course.id}
              className="flex flex-col justify-between gap-3 rounded-list border-2.5 border-ink bg-cream p-3.5 shadow-hard"
            >
              <div className="flex items-center gap-3">
                <CourseGlyph name={course.name} size={40} />
                <div className="min-w-0">
                  <div className="line-clamp-2 text-[15px] font-bold leading-tight">{course.name}</div>
                  <div className="text-[13px] text-muted-foreground">
                    {course.vocabularyCount} Vokabeln · {course.memberCount} Mitglieder
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => handleJoinCourse(course.id)}
                disabled={joinCourseMutation.isPending}
              >
                Beitreten
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <SectionLabel>SCHNELLAKTIONEN</SectionLabel>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: 'Kurse verwalten', to: '/app/courses' },
          { label: '+ Neuer Kurs', to: '/app/courses' },
          { label: 'Statistik', to: '/app/statistics' },
          { label: 'Profil', to: '/app/profile' },
        ].map((a, i) => (
          <button
            key={a.label}
            onClick={() => navigate(a.to)}
            className={`press flex h-[60px] items-center justify-center rounded-[18px] border-2.5 border-ink px-3 text-[15px] font-bold shadow-hard ${i === 0 ? 'bg-marker' : 'bg-cream'}`}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
